import { ExamCenterProctorType, useGetAllExamCentersQuery } from '@exam-center/graphql';
import { Form, Flex, Divider, Button, FormSearchMultipleSelection } from '@shared/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAdminProctorListSearchParams } from '../../hooks';

export const adminProctorFilterSectionFormSchema = z.object({
  proctorType: z
    .object({
      label: z.string(),
      value: z.nativeEnum(ExamCenterProctorType),
    })
    .array(),
  examCenter: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .array(),
});

const PROCTOR_TYPE_OPTIONS = [
  { label: 'ผู้คุมสอบประเภทที่ 1', value: ExamCenterProctorType.Proctor_1 },
  { label: 'ผู้คุมสอบประเภทที่ 2', value: ExamCenterProctorType.Proctor_2 },
  { label: 'ผู้คุมสอบประเภทที่ 3', value: ExamCenterProctorType.Proctor_3 },
];

export type AdminProctorFilterSectionFormType = z.infer<typeof adminProctorFilterSectionFormSchema>;

export default function AdminProctorFilterSection() {
  const [, { changeProctorType, changeExamCenter }] = useAdminProctorListSearchParams();

  const { data, loading } = useGetAllExamCentersQuery();
  const examCenterOptions = useMemo(() => {
    return [...(data?.ecGetAllExamCenters?.examCenters ?? [])]
      .sort((a, b) => a.examCenterId - b.examCenterId)
      .map((examCenter) => ({
        label: examCenter.code,
        value: examCenter.code,
      }));
  }, [data]);

  const formContext = useForm<AdminProctorFilterSectionFormType>({
    defaultValues: {
      proctorType: [],
      examCenter: [],
    },
  });
  const handleResetFilter = () => {
    formContext.reset();
    handleSubmitFilter();
  };

  const handleSubmitFilter = () => {
    const { proctorType, examCenter } = formContext.getValues();
    const mappedProctorType = proctorType.map<ExamCenterProctorType>((type) => type.value);
    const mappedExamCenter = examCenter.map((center) => center.value);

    changeProctorType(mappedProctorType);
    changeExamCenter(mappedExamCenter);
  };

  return (
    <FormProvider {...formContext}>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
            })}
          >
            <FormSearchMultipleSelection
              name='proctorType'
              options={PROCTOR_TYPE_OPTIONS}
              label='ประเภทผู้คุมสอบ'
              placeholder='แสดงทั้งหมด'
              sx={{ width: 259 }}
              slotProps={{
                input: {
                  placeholder:
                    (formContext.watch('proctorType') ?? []).map((item) => item.value).length === 0
                      ? 'แสดงทั้งหมด'
                      : '',
                },
              }}
            />
            <FormSearchMultipleSelection
              name='examCenter'
              options={examCenterOptions}
              label='ศูนย์ทดสอบ'
              loading={loading}
              placeholder='แสดงทั้งหมด'
              sx={{ width: 259 }}
              slotProps={{
                input: {
                  placeholder:
                    (formContext.watch('examCenter') ?? []).map((item) => item.value).length === 0
                      ? 'แสดงทั้งหมด'
                      : '',
                },
              }}
            />
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button
              variant='outlined'
              color='secondary'
              sx={{ width: '100px' }}
              onClick={handleResetFilter}
            >
              Reset
            </Button>
            <Button variant='contained' sx={{ width: '100px' }} onClick={handleSubmitFilter}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
